import React, { useState, useCallback, useRef, useEffect } from "react";
import { ScrollHostContainer } from "./ScrollbarStyled";

interface SCROLL_BAR_PROPS {
  children?: JSX.Element | JSX.Element[];
  needScroll?: boolean;
}
const SCROLL_BOX_MIN_WIDTH = 20;

export default function ScrollBarHost({
  children,
  needScroll,
}: SCROLL_BAR_PROPS) {
  const [scrollBoxWidth, setScrollBoxWidth] = useState(SCROLL_BOX_MIN_WIDTH);
  const [scrollBoxLeft, setScrollBoxLeft] = useState(0);
  const [lastScrollThumbPosition, setScrollThumbPosition] = useState(0);
  const [isDragging, setDragging] = useState(false);
  const scrollHostRef = useRef<any>();

  const handleDocumentMouseUp = useCallback(
    (e) => {
      if (isDragging) {
        e.preventDefault();
        setDragging(false);
      }
    },
    [isDragging]
  );

  const handleDocumentMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
        const scrollHostElement = scrollHostRef.current;
        const { scrollWidth, offsetWidth } = scrollHostElement;

        let deltaX = e.clientX - lastScrollThumbPosition;
        let percentage = deltaX * (scrollWidth / offsetWidth);

        setScrollThumbPosition(e.clientX);
        setScrollBoxLeft(
          Math.min(
            Math.max(0, scrollBoxLeft + deltaX),
            offsetWidth - scrollBoxWidth
          )
        );
        scrollHostElement.scrollLeft = Math.min(
          scrollHostElement.scrollLeft + percentage,
          scrollWidth - offsetWidth
        );
      }
    },
    [isDragging, lastScrollThumbPosition, scrollBoxWidth, scrollBoxLeft]
  );

  const handleScrollThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollThumbPosition(e.clientX);
    setDragging(true);
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollHostRef) {
      return;
    }
    const scrollHostElement = scrollHostRef.current;
    const { scrollLeft, scrollWidth, offsetWidth } = scrollHostElement;

    let newLeft =
      (parseInt(scrollLeft, 10) / parseInt(scrollWidth, 10)) * offsetWidth;
    newLeft = Math.min(newLeft, offsetWidth - scrollBoxWidth);
    setScrollBoxLeft(newLeft);
  }, [scrollBoxWidth]);

  useEffect(() => {
    const scrollHostElement = scrollHostRef.current;
    const { clientWidth, scrollWidth } = scrollHostElement;
    const scrollThumbPercentage = clientWidth / scrollWidth;
    const scrollThumbWidth = Math.max(
      scrollThumbPercentage * clientWidth,
      SCROLL_BOX_MIN_WIDTH
    );
    setScrollBoxWidth(scrollThumbWidth);
    scrollHostElement.addEventListener("scroll", handleScroll, true);
    return function cleanup() {
      scrollHostElement.removeEventListener("scroll", handleScroll, true);
    };
  }, [handleScroll]);

  useEffect(() => {
    //this is handle the dragging on scroll-thumb
    document.addEventListener("mousemove", handleDocumentMouseMove);
    document.addEventListener("mouseup", handleDocumentMouseUp);
    document.addEventListener("mouseleave", handleDocumentMouseUp);
    document.addEventListener("touchstart", handleDocumentMouseMove);
    document.addEventListener("touchmove", handleDocumentMouseMove);
    document.addEventListener("touchend", handleDocumentMouseMove);
    return function cleanup() {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
      document.removeEventListener("mouseup", handleDocumentMouseUp);
      document.removeEventListener("mouseleave", handleDocumentMouseUp);
      document.removeEventListener("touchstart", handleDocumentMouseMove);
      document.removeEventListener("touchmove", handleDocumentMouseMove);
      document.removeEventListener("touchend", handleDocumentMouseMove);
    };
  }, [handleDocumentMouseMove, handleDocumentMouseUp]);
  return (
    <ScrollHostContainer>
      <div className="scroll-host" ref={scrollHostRef}>
        {children}
      </div>
      {needScroll && (
        <div className="scroll-bar">
          <div
            className="scroll-thumb"
            style={{ width: scrollBoxWidth, left: scrollBoxLeft }}
            onMouseDown={(e) => {
              handleScrollThumbMouseDown(e);
            }}
            onTouchStart={(e) => {
              handleScrollThumbMouseDown(e);
            }}
            onTouchMove={(e) => {
              handleScrollThumbMouseDown(e);
            }}
          />
        </div>
      )}
    </ScrollHostContainer>
  );
}
